import { AudioData } from 'src/app/shared/interfaces';

export function replaceSpecialCharacters(
  text: string,
  document: Document
): string {
  const specialCharacters: { [key: string]: string } = {
    '<': 'less than ',
    '>': 'greater than ',
    '\\*': '',
    '[\r\n]+': ' ',
    '(\\\\_)+': 'blank',
    '[\\s]{1,}': ' ',
    '[“”]': '"',
    '[‘’]': "'",
  };
  // Strip HTML
  const capture = document.createElement('DIV');
  capture.innerHTML = text;
  return [...Object.entries(specialCharacters)]
    .reduce<string>(
      (acc: string, [key, value]: [string, string]) =>
        acc.replace(new RegExp(key, 'g'), value),
      capture.textContent || capture.innerText || ''
    )
    .trim();
}

/**
 * Wrap a promise so that it rejects after a specified timeout.
 * @param promise promise to wrap
 * @param ms timeout in ms after which the promise should reject
 * @returns wrapped promise
 */
export function promiseTimeout<Type>(
  promise: Promise<Type>,
  ms: number
): Promise<Type> {
  const timeout = new Promise<Type>((resolve, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject(new Error(`Timed out in ${ms} ms.`));
    }, ms);
  });

  return Promise.race([promise, timeout]);
}

/**
 * Remove an ellipsis if it appears at the very beginning of a string.
 * @param string string to process
 * @returns processed string
 */
export function removeOpeningEllipsis(string: string): string {
  const ellipsisAtStartRegex = /^\.\.\./;
  return string?.replace(ellipsisAtStartRegex, '') ?? '';
}

/**
 * Combine available input to get a valid unique key for the AudioService sounds map.
 * @param audioData input
 * @returns key
 */
export function getSoundsMapKey(audioData: AudioData): string {
  return `${audioData.id ?? 'id'}-${audioData.text?.replace(/\s/g, '') ?? ''}`;
}

/**
 * Generate a unique id for an audio resource
 * @param audioData audio data to generate an id for
 * @returns unique id for the audio resource
 */
export function generateAudioId(audioData: AudioData, index?: number): string {
  // Sanitize and shorten the text
  const sanitizedText = audioData.text
    ? ((): string => {
        const cleaned = audioData.text
          .replace(/[^a-zA-Z0-9]/g, '')
          .toLowerCase();
        return cleaned.length > 24
          ? `${cleaned.slice(0, 12)}-${cleaned.slice(-12)}`
          : cleaned;
      })()
    : 'sfx'; // limit length for readability

  const baseId =
    index !== undefined ? `${audioData.id}-${index}` : audioData.id;

  // Generate a short hash
  const hash = crypto.randomUUID().slice(0, 8);

  const audioId = `${baseId}-${sanitizedText}-${hash}`;
  return audioId;
}

/**
 * Split text into sentences while preserving tokens and staying within character limits
 * @param text The text to split
 * @returns Array of text chunks split by sentences
 */
export const MAX_TTS_CHARS = 500;

// eslint-disable-next-line complexity
export function splitTextBySentences(
  text: string,
  maxPerSentenceTTSChars = 150
): string[] {
  if (text.length <= MAX_TTS_CHARS) {
    return [text];
  }

  // More robust sentence detection that handles abbreviations and line breaks
  const sentences: string[] = [];

  // First, split by line breaks to handle poems/prose
  const lines = text.split(/\r?\n/);

  // Then split each line by sentences if it contains sentence-ending punctuation
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue; // Skip empty lines

    // Check if line contains sentence-ending punctuation
    if (/[.!?]/.test(trimmedLine)) {
      // Split this line by sentences, preserving abbreviations
      const sentenceRegex =
        /(?<!\b(?:Mr|Mrs|Dr|Prof|Sr|Jr|vs|etc|i\.e|e\.g)\.)(?<=\.|!|\?)\s+(?=[A-Z])/g;
      let lastIndex = 0;
      let match;

      while ((match = sentenceRegex.exec(trimmedLine)) !== null) {
        const sentence = trimmedLine
          .substring(lastIndex, match.index + 1)
          .trim();
        if (sentence) {
          sentences.push(sentence);
        }
        lastIndex = match.index + 1;
      }

      // Add the remaining part of the line
      const lastSentence = trimmedLine.substring(lastIndex).trim();
      if (lastSentence) {
        sentences.push(lastSentence);
      }
    } else {
      // Line doesn't contain sentence punctuation, treat the whole line as one unit
      sentences.push(trimmedLine);
    }
  }

  // Filter out any empty sentences and log each one
  const finalSentences = [];

  // join adjacent sentences with a period(.) if the length is less than maxPerSentenceTTSChars
  let currentSentence = '';
  for (let sentence of sentences) {
    sentence = sentence.trim();
    if (!sentence.length) continue; // Skip empty sentences
    if (
      currentSentence.length + sentence.length + 1 <=
      maxPerSentenceTTSChars
    ) {
      currentSentence += (currentSentence ? ' ' : '') + sentence;
    } else {
      if (currentSentence) {
        finalSentences.push(currentSentence);
        currentSentence = sentence; // Start a new sentence
      } else {
        finalSentences.push(sentence); // If currentSentence is empty, just push the sentence
      }
    }
  }
  // Push any remaining sentence
  if (currentSentence) {
    finalSentences.push(currentSentence);
  }

  return finalSentences;
}
